package com.schoolconnect.app.feature.notices

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.*
import com.schoolconnect.app.designsystem.AppPageHeader
import com.schoolconnect.app.designsystem.AppPageSubtitle
import com.schoolconnect.app.designsystem.list.*
import com.schoolconnect.app.feature.attendance.ParentAttendanceChild
import com.schoolconnect.app.feature.common.*
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.*
import org.jetbrains.compose.resources.painterResource

@Composable
fun ParentNoticesScreen(state: ParentNoticesUiState, onEvent: (ParentNoticesUiEvent) -> Unit, shellRoleCode: String, onChangeRole: () -> Unit, onLogout: () -> Unit) {
    var profileExpanded by remember { mutableStateOf(false) }
    var rowsMenu by remember { mutableStateOf(false) }
    var customRangeVisible by remember { mutableStateOf(false) }
    val window = pageWindow(state.pageIndex, state.pageSize, state.matchingCount)
    val rows = state.rows
    AdminListPageScaffold(
        title = "Notices", subtitle = "Stay updated with school announcements.", backgroundColor = AppBackground, titleColor = AppPageHeader, subtitleColor = AppPageSubtitle,
        headerActions = { ShellTopRightActions(shellRoleLabel(shellRoleCode), shellRoleSubtitle(shellRoleCode), shellAvatarInitials(shellRoleLabel(shellRoleCode)), profileExpanded, { profileExpanded = it }, onChangeRole, onLogout) },
        summaryCards = { ParentNoticeMetric("Total Notices", state.totalCount.toString(), "Across all notices", Res.drawable.file_spreadsheet, AppPrimary, AppPrimarySoft); ParentNoticeMetric("Active", state.activeCount.toString(), "Currently active", Res.drawable.megaphone, AppSuccess, AppSuccessSoft); ParentNoticeMetric("Expired", state.expiredCount.toString(), "Past expiry date", Res.drawable.ban, AppDanger, AppDangerSoft); ParentNoticeMetric("Today", state.todayCount.toString(), "Published today", Res.drawable.calendar, Color(0xFFFF7A00), Color(0xFFFFF0E3)) },
        toolbar = { ParentNoticeFilters(state, onEvent, onCustomRangeRequested = { customRangeVisible = true }) },
        table = { modifier -> AdminListTableCard(modifier = modifier, minContentWidth = 1060.dp,
            header = { AdminListSortHeaderRow(listOf(
                AdminListSortColumn("Notice Title", 1.5f, state.sortField == NoticesSortField.TITLE, state.sortAscending) { onEvent(ParentNoticesUiEvent.SortChanged(NoticesSortField.TITLE)) },
                AdminListSortColumn("Applicable To", 1.25f, state.sortField == NoticesSortField.AUDIENCE, state.sortAscending) { onEvent(ParentNoticesUiEvent.SortChanged(NoticesSortField.AUDIENCE)) },
                AdminListSortColumn("Published On", 1f, state.sortField == NoticesSortField.PUBLISHED_DATE, state.sortAscending) { onEvent(ParentNoticesUiEvent.SortChanged(NoticesSortField.PUBLISHED_DATE)) },
                AdminListSortColumn("Expiry Date", 1f, state.sortField == NoticesSortField.EXPIRY_DATE, state.sortAscending) { onEvent(ParentNoticesUiEvent.SortChanged(NoticesSortField.EXPIRY_DATE)) },
                AdminListSortColumn("Status", .8f, state.sortField == NoticesSortField.STATUS, state.sortAscending) { onEvent(ParentNoticesUiEvent.SortChanged(NoticesSortField.STATUS)) }
            ), actionsWeight = .65f) },
            body = { if (state.isLoading) AdminListTableShimmer(columns = listOf(AdminListHeaderColumn("Notice Title", 1.5f), AdminListHeaderColumn("Applicable To", 1.25f), AdminListHeaderColumn("Published On", 1f), AdminListHeaderColumn("Expiry Date", 1f), AdminListHeaderColumn("Status", .8f), AdminListHeaderColumn("Actions", .65f)), rowCount = state.pageSize) else if (state.errorMessage.isNotBlank()) AppDialogErrorMessage(state.errorMessage) else if (rows.isEmpty()) Box(Modifier.fillMaxWidth().heightIn(min = 220.dp), contentAlignment = Alignment.Center) { Text("No notices found", color = AppNavy, fontWeight = FontWeight.Bold) } else AdminListTableBody(items = rows, key = { it.id }, modifier = Modifier.fillMaxWidth()) { row ->
                Column(Modifier.fillMaxWidth()) {
                    Row(Modifier.fillMaxWidth().onViewDoubleClick { onEvent(ParentNoticesUiEvent.ViewRequested(row)) }.padding(horizontal = 18.dp, vertical = 15.dp), verticalAlignment = Alignment.CenterVertically) {
                        NoticeCell(row.title, 1.5f); NoticeCell(row.audience, 1.25f); NoticeCell(row.publishedOn, 1f); NoticeCell(row.expiryDate, 1f); NoticeCell(row.status, .8f)
                        Box(Modifier.weight(.65f), contentAlignment = Alignment.Center) { AdminListViewActionButton(onClick = { onEvent(ParentNoticesUiEvent.ViewRequested(row)) }, contentDescription = "View notice") }
                    }
                    if (row != rows.last()) HorizontalDivider(color = AppBorder)
                }
            } },
            footer = { AdminListFooter(pageText = pageFooterText(window, "notices"), pageIndex = window.pageIndex, pageCount = window.pageCount, onPageChanged = { onEvent(ParentNoticesUiEvent.PageChanged(it)) }, pageSize = state.pageSize, rowsPerPageExpanded = rowsMenu, onRowsPerPageToggle = { rowsMenu = !rowsMenu }, onRowsPerPageDismiss = { rowsMenu = false }, onPageSizeChanged = { onEvent(ParentNoticesUiEvent.PageSizeChanged(it)) }) }) },
        bottomAction = { Spacer(Modifier.weight(1f)) }
    )
    val row = state.viewingNotice
    ViewNoticeDialog(
        notice = row,
        detail = state.viewingDetail,
        isLoading = state.isViewingLoading,
        errorMessage = state.viewingError,
        onDismiss = { onEvent(ParentNoticesUiEvent.ViewDismissed) },
        onView = {},
        loadDetail = false
    )
    if (customRangeVisible) {
        AppDialogDatePickerDialog(
            value = state.periodFrom,
            onDismiss = { customRangeVisible = false },
            onDateSelected = {},
            colors = AppDialogDefaultColors,
            dateRangeSelection = true,
            dateRangeStartValue = state.periodFrom,
            dateRangeEndValue = state.periodTo,
            onDateRangeSelected = { from, to ->
                customRangeVisible = false
                onEvent(ParentNoticesUiEvent.CustomPeriodApplied(from, to))
            }
        )
    }
}

@Composable private fun RowScope.ParentNoticeMetric(title: String, value: String, subtitle: String, icon: org.jetbrains.compose.resources.DrawableResource, accent: Color, bg: Color) = AdminListSummaryCard(title, value, subtitle, painterResource(icon), accent, bg, AppMuted, modifier = Modifier.weight(1f))
@Composable private fun RowScope.NoticeCell(value: String, weight: Float) = AdminListTableBodyText(value, Modifier.weight(weight))
@Composable
private fun ParentNoticeFilters(
    state: ParentNoticesUiState,
    onEvent: (ParentNoticesUiEvent) -> Unit,
    onCustomRangeRequested: () -> Unit
) {
    AppCard(
        Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(10.dp)),
        containerColor = Color.White
    ) {
        Row(
            Modifier.fillMaxWidth().padding(16.dp),
            horizontalArrangement = Arrangement.spacedBy(14.dp),
            verticalAlignment = Alignment.Bottom
        ) {
            Column(Modifier.weight(1.35f), verticalArrangement = Arrangement.spacedBy(7.dp)) {
                AppText(
                    "Search by Notice Title",
                    color = AppNavy,
                    style = MaterialTheme.typography.labelLarge,
                    fontWeight = FontWeight.Bold
                )
                AdminListSearchField(
                    state.searchQuery,
                    { onEvent(ParentNoticesUiEvent.SearchChanged(it)) },
                    "Search notice title…",
                    width = 340.dp,
                    surfaceColor = Color.White,
                    borderColor = AppBorder,
                    textColor = AppNavy
                )
            }
            ParentNoticeDropdown(
                "Period",
                state.period,
                listOf("All", "Today", "Last 7 Days", "This Month", "Custom Range"),
                Modifier.weight(1f)
            ) { selected ->
                if (selected.equals("Custom Range", ignoreCase = true)) {
                    onCustomRangeRequested()
                } else {
                    onEvent(ParentNoticesUiEvent.PeriodChanged(selected))
                }
            }
            ParentNoticeDropdown(
                "Status",
                state.status,
                listOf("All", "Active", "Expired"),
                Modifier.weight(1f)
            ) { onEvent(ParentNoticesUiEvent.StatusChanged(it)) }
            AppOutlinedButton(
                onClick = { onEvent(ParentNoticesUiEvent.FiltersCleared) },
                modifier = Modifier.width(116.dp).height(44.dp),
                colors = ButtonDefaults.outlinedButtonColors(contentColor = AppPrimary),
                shape = RoundedCornerShape(8.dp)
            ) {
                Icon(painterResource(Res.drawable.refresh_cw), null, Modifier.size(17.dp), tint = AppPrimary)
                Spacer(Modifier.width(6.dp))
                Text("Clear", color = AppPrimary, fontWeight = FontWeight.SemiBold, fontSize = 14.sp)
            }
        }
    }
}
@Composable private fun ParentNoticeDropdown(label: String, value: String, options: List<String>, modifier: Modifier, onSelected: (String) -> Unit) =
    AppLabeledDropdownField(label, value, options, onSelected, modifier, includeAllOption = false)
